const GECKO_FORM_UUID = "21FO0162thi4ci00187byz1wpl";
const GECKO_API_URL = "https://api-eu.geckoform.com/responses";
const GECKO_FORM_URL = `https://api-eu.geckoform.com/forms/${GECKO_FORM_UUID}/public`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class CompetitionForm {
	constructor(form) {
		this.form = form;
		this.modal = this.form.closest(".modal");
		this.submitBtn = this.form.querySelector('button[type="submit"]');
		this.feedbackEl = this.form.querySelector(".competition-form-feedback");
		this.initialButtonText = this.submitBtn ? this.submitBtn.innerHTML : "Sign up";
		this.formConfig = null;
		this.recaptchaReady = null;
		this.recaptchaKey = null;

		this.events();
	}

	events() {
		this.form.addEventListener("submit", (e) => this.handleSubmit(e));
		this.form.addEventListener("input", (e) => this.clearFieldError(e.target));
		this.form.addEventListener("change", (e) => this.clearFieldError(e.target));

		if (this.modal) {
			this.modal.addEventListener("hidden.bs.modal", () => this.resetOnModalClose());
		}
	}

	resetOnModalClose() {
		this.clearFieldErrors();
		this.clearFeedback();

		if (this.submitBtn) {
			this.submitBtn.disabled = false;
			this.submitBtn.innerHTML = this.initialButtonText;
		}
	}

	async getFormConfig() {
		if (this.formConfig) {
			return this.formConfig;
		}

		const response = await fetch(GECKO_FORM_URL, {
			headers: { Accept: "application/json" },
		});

		if (!response.ok) {
			throw new Error("Unable to load form configuration");
		}

		const data = await response.json();
		this.formConfig = data.form;
		return this.formConfig;
	}

	isCaptchaRequired(formConfig) {
		return Boolean(formConfig?.protected && formConfig?.captcha?.key);
	}

	loadGeckoRecaptcha(siteKey) {
		if (this.recaptchaReady && this.recaptchaKey === siteKey) {
			return this.recaptchaReady;
		}

		this.recaptchaKey = siteKey;
		this.recaptchaReady = new Promise((resolve, reject) => {
			const existing = document.querySelector(`script[data-gecko-recaptcha="${siteKey}"]`);
			if (existing && window.grecaptcha?.execute) {
				window.grecaptcha.ready(() => resolve(window.grecaptcha));
				return;
			}

			const script = document.createElement("script");
			script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
			script.async = true;
			script.dataset.geckoRecaptcha = siteKey;
			script.onload = () => {
				if (!window.grecaptcha?.ready) {
					reject(new Error("reCAPTCHA failed to load"));
					return;
				}
				window.grecaptcha.ready(() => resolve(window.grecaptcha));
			};
			script.onerror = () => reject(new Error("reCAPTCHA failed to load"));
			document.head.appendChild(script);
		});

		return this.recaptchaReady;
	}

	async getCaptchaPayload(formConfig) {
		if (!this.isCaptchaRequired(formConfig)) {
			return null;
		}

		const grecaptcha = await this.loadGeckoRecaptcha(formConfig.captcha.key);
		const token = await grecaptcha.execute(formConfig.captcha.key, { action: "submit" });

		return {
			token,
			provider: formConfig.captcha.provider || "recaptcha",
		};
	}

	setBusy(isBusy) {
		if (!this.submitBtn) {
			return;
		}
		this.submitBtn.disabled = isBusy;
		this.submitBtn.classList.toggle("running", isBusy);
		this.submitBtn.innerHTML = isBusy ? "Sending…" : this.initialButtonText;
	}

	clearFeedback() {
		if (!this.feedbackEl) {
			return;
		}
		this.feedbackEl.style.display = "none";
		this.feedbackEl.classList.remove("is-invalid");
		this.feedbackEl.innerHTML = "";
	}

	setFeedback(message, isError = false) {
		if (!this.feedbackEl) {
			return;
		}
		this.feedbackEl.classList.toggle("is-invalid", isError);
		this.feedbackEl.innerHTML = message;
		this.feedbackEl.style.display = "block";
	}

	clearFieldErrors() {
		this.form.querySelectorAll(".is-invalid").forEach((el) => {
			if (el === this.feedbackEl) {
				return;
			}
			el.classList.remove("is-invalid");
			el.removeAttribute("aria-invalid");
			el.removeAttribute("aria-describedby");
		});
		this.form.querySelectorAll(".invalid-feedback").forEach((el) => el.remove());
	}

	clearFieldError(input) {
		if (!input || !input.name) {
			return;
		}

		const field = input.closest(".field");
		const feedbackId = `${input.id || input.name}-validation`;
		const feedback = this.form.querySelector(`#${CSS.escape(feedbackId)}`);

		input.classList.remove("is-invalid");
		input.removeAttribute("aria-invalid");
		input.removeAttribute("aria-describedby");
		field?.classList.remove("is-invalid");
		feedback?.remove();
	}

	setFieldError(input, message) {
		if (!input) {
			return;
		}

		const field = input.closest(".field") || input.closest(".form-check")?.parentElement;
		const feedbackId = `${input.id || input.name}-validation`;

		input.classList.add("is-invalid");
		input.setAttribute("aria-invalid", "true");
		input.setAttribute("aria-describedby", feedbackId);
		field?.classList.add("is-invalid");

		if (!this.form.querySelector(`#${CSS.escape(feedbackId)}`)) {
			const feedback = document.createElement("p");
			feedback.className = "invalid-feedback";
			feedback.id = feedbackId;
			feedback.textContent = message;
			feedback.style.display = "block";

			if (input.type === "checkbox" && input.closest(".form-check")) {
				input.closest(".form-check").insertAdjacentElement("afterend", feedback);
			} else if (field) {
				field.appendChild(feedback);
			} else {
				input.insertAdjacentElement("afterend", feedback);
			}
		}
	}

	getValue(name) {
		const input = this.form.querySelector(`[name="${name}"]`);
		if (!input) {
			return "";
		}
		if (input.type === "checkbox") {
			return input.checked ? String(input.value || "1") : "";
		}
		return String(input.value || "").trim();
	}

	validate() {
		this.clearFieldErrors();
		this.clearFeedback();

		const errors = [];
		const firstName = this.getValue("first_name");
		const lastName = this.getValue("last_name");
		const email = this.getValue("email");
		const emailConfirm = this.getValue("email_confirm");

		const firstNameInput = this.form.querySelector('[name="first_name"]');
		const lastNameInput = this.form.querySelector('[name="last_name"]');
		const emailInput = this.form.querySelector('[name="email"]');
		const emailConfirmInput = this.form.querySelector('[name="email_confirm"]');

		if (!firstName) {
			const message = "Please enter your first name";
			this.setFieldError(firstNameInput, message);
			errors.push(message);
		}

		if (!lastName) {
			const message = "Please enter your last name";
			this.setFieldError(lastNameInput, message);
			errors.push(message);
		}

		if (!email) {
			const message = "Please enter your email address";
			this.setFieldError(emailInput, message);
			errors.push(message);
		} else if (!EMAIL_PATTERN.test(email)) {
			const message = "Please enter a valid email address";
			this.setFieldError(emailInput, message);
			errors.push(message);
		}

		if (!emailConfirm) {
			const message = "Please confirm your email address";
			this.setFieldError(emailConfirmInput, message);
			errors.push(message);
		} else if (email && emailConfirm !== email) {
			const message = "Email addresses must match";
			this.setFieldError(emailConfirmInput, message);
			errors.push(message);
		}

		if (errors.length) {
			this.setFeedback("Please correct the errors below and try again.", true);
			const firstInvalid = this.form.querySelector(".field .is-invalid, .form-check-input.is-invalid");
			firstInvalid?.focus();
			return false;
		}

		return true;
	}

	showError(message) {
		this.setFeedback(message, true);
	}

	showSuccess(message) {
		this.clearFieldErrors();
		this.setFeedback(message, false);

		this.form.querySelectorAll("input:not([type='hidden']), textarea, select").forEach((el) => {
			if (el.type === "checkbox" || el.type === "radio") {
				el.checked = false;
			} else {
				el.value = "";
			}
			el.disabled = false;
		});

		if (this.submitBtn) {
			this.submitBtn.disabled = true;
			this.submitBtn.innerHTML = "Signed up";
		}
	}

	buildConsentPayload() {
		return [
			{ consent: 1, granted: true },
			{ consent: 2, granted: null },
			{ consent: 3, granted: null },
			{ consent: 4, granted: null },
		];
	}

	buildPayload(captcha = null) {
		const firstName = this.getValue("first_name");
		const lastName = this.getValue("last_name");
		const email = this.getValue("email");
		const emailConfirm = this.getValue("email_confirm");
		const source = this.getValue("source");
		const keepUpdated = this.form.querySelector('[name="keep_updated"]');

		const payload = {
			uuid: GECKO_FORM_UUID,
			fields: {
				field608: null,
				field607: {
					first_name: firstName,
					last_name: lastName,
				},
				field455: [email, emailConfirm],
				field465: null,
				field461: source || "Spotlight_clearing_2026",
			},
			event_id: null,
			user_id: null,
			contact_id: null,
			conversation_id: null,
			participant_id: null,
			knowledge_base_item_id: null,
			data_capture_action_id: null,
			data_capture_invocation_id: null,
			utm_source: null,
			utm_medium: null,
			utm_campaign: null,
			utm_term: null,
			utm_content: null,
			draft_id: null,
		};

		if (keepUpdated?.checked) {
			payload.fields.field610 = this.buildConsentPayload();
		}

		if (captcha) {
			payload.captcha = captcha;
		}

		return payload;
	}

	async handleSubmit(event) {
		event.preventDefault();

		if (!this.validate()) {
			return;
		}

		this.setBusy(true);

		try {
			const formConfig = await this.getFormConfig();
			const captcha = await this.getCaptchaPayload(formConfig);
			const payload = this.buildPayload(captcha);

			const response = await fetch(GECKO_API_URL, {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});

			const data = await response.json().catch(() => ({}));

			if (!response.ok) {
				const message =
					data?.message ||
					data?.error ||
					(Array.isArray(data?.messages) ? data.messages.flat().join(" ") : null) ||
					"Sorry, we couldn’t submit your entry. Please try again.";
				this.showError(message);
				this.setBusy(false);
				return;
			}

			this.showSuccess("Thanks — you’re signed up and entered into the prize draw.");
		} catch (error) {
			this.showError("Sorry, we couldn’t submit your entry. Please try again.");
			this.setBusy(false);
		}
	}
}
